// Selection logic for the bulk archive & restore dialog
// (PeerGroupBatchDialog.vue), kept free of Vue so it can be tested directly.
//
// Selections are lists of peer group ids.  They outlive any one search, so a
// selection can hold groups the current query hides.

import { OrgPeerGroupBase } from '@/models/orgStructure';
import { DEFAULT_PEER_GROUP_SORT, PeerGroupSort } from '@/preferences';
import { peerGroupComparator } from '@/peerGroupSort';
import * as Utils from '@/utils';

export type SelectAllState = 'none' | 'some' | 'all';

/**
 * LTI-managed groups take their active status from the LMS, which would undo
 * an archive or restore made here on its next sync.
 */
export const isSelectable = (pg: OrgPeerGroupBase): boolean => !pg.isLtiManaged;

/**
 * The groups with the given status that match `query`, in `sort` order.
 */
export const matchGroups = <T extends OrgPeerGroupBase>(
  groups: T[],
  isActive: boolean,
  query: string,
  sort: PeerGroupSort = DEFAULT_PEER_GROUP_SORT
): T[] => {
  const withStatus = groups.filter(pg => pg.isActive === isActive);
  const matching = query
    ? Utils.findTerm(query, withStatus, pg => [
        pg.name,
        pg.section,
        pg.description
      ])
    : withStatus;

  return matching.slice().sort(peerGroupComparator(sort));
};

export const selectableIds = (matching: OrgPeerGroupBase[]): number[] =>
  matching.filter(isSelectable).map(pg => pg.peerGroupId);

export const selectAllState = (
  matching: OrgPeerGroupBase[],
  selected: number[]
): SelectAllState => {
  const ids = selectableIds(matching);
  const chosen = ids.filter(id => selected.includes(id)).length;

  if (chosen === 0) {
    return 'none';
  }

  return chosen === ids.length ? 'all' : 'some';
};

/**
 * Selects every selectable match, or clears them when all are already
 * selected.  Selected groups outside `matching` are left alone either way.
 */
export const toggleAll = (
  matching: OrgPeerGroupBase[],
  selected: number[]
): number[] => {
  const ids = selectableIds(matching);

  if (selectAllState(matching, selected) === 'all') {
    return selected.filter(id => !ids.includes(id));
  }

  return [...selected, ...ids.filter(id => !selected.includes(id))];
};

export const toggleOne = (selected: number[], id: number): number[] =>
  selected.includes(id)
    ? selected.filter(other => other !== id)
    : [...selected, id];

const groups = (count: number) => (count === 1 ? '1 group' : `${count} groups`);

/**
 * The toast text after archiving (`isActive` false) or restoring groups.
 */
export const batchResultMessage = (
  isActive: boolean,
  succeeded: number,
  failed: number
): string => {
  if (succeeded === 0) {
    return `Failed to ${isActive ? 'restore' : 'archive'} ${groups(failed)}.`;
  }

  const done = `${isActive ? 'Restored' : 'Archived'} ${groups(succeeded)}.`;

  return failed > 0 ? `${done} ${failed} failed.` : done;
};

/**
 * How many selected groups the current matches don't show.
 */
export const hiddenCount = (
  matching: OrgPeerGroupBase[],
  selected: number[]
): number => {
  const shown = new Set(matching.map(pg => pg.peerGroupId));

  return selected.filter(id => !shown.has(id)).length;
};
